import { escapeHtml } from "../../lib/escape.ts";
import { lucideIcon } from "../../ui/icons.ts";
import { requirementNotes, type RequirementFacts } from "./requirements.ts";
import type { TutorialLesson } from "./model.ts";

export interface LessonRow {
  caption: string;
  dimmed: boolean;
  done: boolean;
  current: boolean;
}

export function lessonRow(lesson: TutorialLesson, current: string, done: readonly string[], facts: RequirementFacts): LessonRow {
  const notes = requirementNotes(lesson.requires ?? [], facts);
  return {
    caption: [lesson.duration, ...notes.map((note) => note.text)].filter((part) => part !== undefined && part !== "").join(" · "),
    dimmed: notes.some((note) => note.unmet),
    done: done.includes(lesson.id),
    current: lesson.id === current,
  };
}

function renderRow(lesson: TutorialLesson, row: LessonRow): string {
  const mark = row.done ? `<span class="tutorial-lesson-mark" role="img" aria-label="Done">${lucideIcon("circle-check", 14)}</span>` : '<span class="tutorial-lesson-mark"></span>';
  const caption = row.caption === "" ? "" : `<span class="tutorial-lesson-caption">${escapeHtml(row.caption)}</span>`;
  return `<button type="button" data-tutorial-action="lesson" data-lesson="${escapeHtml(lesson.id)}" data-dimmed="${row.dimmed}"${row.current ? ' aria-current="step"' : ""}>${mark}<span class="tutorial-lesson-text"><span class="tutorial-lesson-title">${escapeHtml(lesson.title)}</span>${caption}</span></button>`;
}

// The guided course sits under Get started and the optional chapters under
// Explore Deck; a catalog without optional lessons keeps the flat list.
// Every lesson is listed whether or not the installation can run it.
export function renderLessonList(
  catalog: readonly TutorialLesson[],
  current: string,
  done: readonly string[],
  facts: RequirementFacts,
): string {
  const button = (lesson: TutorialLesson) => renderRow(lesson, lessonRow(lesson, current, done, facts));
  const started = catalog.filter((entry) => !entry.optional);
  const explore = catalog.filter((entry) => entry.optional);
  if (explore.length === 0) return started.map(button).join("");
  return [
    started.length > 0 ? `<p class="tutorial-lesson-group">Get started</p>${started.map(button).join("")}` : "",
    `<p class="tutorial-lesson-group">Explore Deck</p>${explore.map(button).join("")}`,
  ].filter(Boolean).join("");
}
